// src/components/TiKaneSenpViewModal.jsx
import React, { useEffect, useMemo, useState } from 'react';
import {
  Modal,
  Button,
  Row,
  Col,
  Spinner,
  Alert,
  Table,
  Badge,
} from 'react-bootstrap';
import tikaneSenpService from '../services/tikaneSenpService';
import transactionTikaneSenpService from '../services/transactionTikaneSenpService';
import ViewPdfButton from './ViewPdfButton';

export default function TiKaneSenpViewModal({ show, onHide, tiKaneSenpId }) {
  const [loading, setLoading] = useState(false);
  const [acc, setAcc] = useState(null);
  const [txs, setTxs] = useState([]);
  const [error, setError] = useState('');

  const fmtMoney = (n) => Number(n || 0).toFixed(2);
  const fmtDate = (d) => (d ? new Date(d).toLocaleDateString() : '—');
  const fmtDateTime = (d) => (d ? new Date(d).toLocaleString() : '—');

  useEffect(() => {
    if (!show || !tiKaneSenpId) return;

    let cancelled = false;

    (async () => {
      try {
        setLoading(true);
        setError('');
        setAcc(null);
        setTxs([]);

        const account = await tikaneSenpService.get(tiKaneSenpId);

        let txData = { transactions: [] };
        try {
          txData = await transactionTikaneSenpService.listByAccount(tiKaneSenpId);
        } catch (e) {
          console.error('Erreur chargement transactions TiKaneSenp:', e);
        }

        if (cancelled) return;

        const merged = {
          ...account,
          balance:
            typeof txData.balance !== 'undefined' && txData.balance !== null
              ? txData.balance
              : account.balance,
        };

        setAcc(merged);
        setTxs(txData.transactions || []);
      } catch (e) {
        console.error(e);
        if (!cancelled) {
          const msg =
            e?.response?.data?.message ||
            e?.message ||
            'Chargement impossible.';
          setError(msg);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [show, tiKaneSenpId]);

  const { sumDeposit, sumWithdraw } = useMemo(() => {
    let dep = 0;
    let wit = 0;
    txs.forEach((t) => {
      const amt = Number(t.amount || 0);
      if (t.type === 'DEPOSIT') dep += amt;
      if (t.type === 'WITHDRAW') wit += amt;
    });
    return { sumDeposit: dep, sumWithdraw: wit };
  }, [txs]);

  const isMinor = !!acc?.minorId;
  const client = acc?.client || null;
  const minor = acc?.minor || null;

  const getDocument = () => ({
    title: `Fiche compte Ti Kanè Senp ${acc?.accountNumber ? `#${acc.accountNumber}` : ''}`.trim(),
    sections: [
      {
        title: 'Compte',
        rows: [
          { label: 'ID', value: acc?.id || '—' },
          { label: 'Numéro de compte', value: acc?.accountNumber || '—' },
          { label: 'Type', value: isMinor ? 'Compte mineur' : 'Compte client adulte' },
          { label: 'Statut', value: acc?.status || '—' },
          { label: 'Créé le', value: fmtDateTime(acc?.createdAt) },
          { label: 'Mis à jour le', value: fmtDateTime(acc?.updatedAt) },
          { label: 'Solde actuel', value: fmtMoney(acc?.balance) },
          { label: 'Total dépôts', value: fmtMoney(sumDeposit) },
          { label: 'Total retraits', value: fmtMoney(sumWithdraw) },
        ],
      },
      !isMinor
        ? {
            title: 'Client',
            rows: [
              { label: 'ID client', value: acc?.clientId || '—' },
              { label: 'Nom', value: client ? `${client.firstName || ''} ${client.lastName || ''}`.trim() : '—' },
              { label: 'NIF/CIN', value: client?.nifCin || '—' },
              { label: 'Téléphone', value: client?.phone || '—' },
              { label: 'Email', value: client?.email || '—' },
              { label: 'Adresse', value: client?.address || '—' },
            ],
          }
        : {
            title: 'Mineur',
            rows: [
              { label: 'ID mineur', value: acc?.minorId || '—' },
              { label: 'Nom', value: minor ? `${minor.firstName || ''} ${minor.lastName || ''}`.trim() : '—' },
              { label: 'Date de naissance', value: minor?.birthDate ? fmtDate(minor.birthDate) : '—' },
              { label: 'Email', value: minor?.email || '—' },
              { label: 'Téléphone', value: minor?.phone || '—' },
              { label: 'Relation', value: minor?.relation || '—' },
              { label: 'Parent (ID)', value: minor?.parentId || '—' },
              { label: 'Notes', value: minor?.notes || '—' },
            ],
          },
      {
        title: 'Historique des transactions',
        rows:
          txs.length > 0
            ? txs.map((t, idx) => ({
                label: `Transaction ${idx + 1}`,
                value: `ID: ${t.id} | Date: ${t.createdAt ? fmtDateTime(t.createdAt) : ''} | Type: ${t.type} | Montant: ${fmtMoney(t.amount)} | Solde après: ${fmtMoney(t.balanceAfter)} | Code: ${t.codeTransaction || '—'} | Note: ${t.description || ''}`,
              }))
            : [{ label: 'Transactions', value: 'Aucune transaction enregistrée.' }],
      },
    ],
  });

  return (
    <Modal show={show} onHide={onHide} size="xl" backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>
          Détails du compte {acc?.accountNumber ? `#${acc.accountNumber}` : ''}
          {' '}
          {isMinor ? (
            <Badge bg="warning" text="dark">
              Mineur
            </Badge>
          ) : (
            <Badge bg="info" text="dark">
              Adulte
            </Badge>
          )}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {loading ? (
          <div className="text-center my-5">
            <Spinner animation="border" />
          </div>
        ) : error ? (
          <Alert variant="danger">{error}</Alert>
        ) : !acc ? (
          <Alert variant="warning">Aucune donnée.</Alert>
        ) : (
          <>
            <Row className="mb-3">
              <Col md={6}>
                <h5 className="mb-3">Informations du compte</h5>
                <div><strong>ID :</strong> {acc.id}</div>
                <div><strong>N° compte :</strong> {acc.accountNumber}</div>
                <div><strong>Type :</strong> {isMinor ? 'Compte mineur' : 'Compte client adulte'}</div>
                <div><strong>Statut :</strong> {acc.status}</div>
                <div><strong>Créé le :</strong> {fmtDateTime(acc.createdAt)}</div>
                <div><strong>Mis à jour le :</strong> {fmtDateTime(acc.updatedAt)}</div>
              </Col>

              <Col md={6}>
                <h5 className="mb-3">Solde et mouvements</h5>
                <div><strong>Solde actuel :</strong> {fmtMoney(acc.balance)}</div>
                <div><strong>Total dépôts :</strong> {fmtMoney(sumDeposit)}</div>
                <div><strong>Total retraits :</strong> {fmtMoney(sumWithdraw)}</div>
              </Col>
            </Row>

            <hr />

            <Row className="mb-3">
              {!isMinor ? (
                <Col md={7}>
                  <h5 className="mb-3">Client</h5>
                  <div><strong>ID client :</strong> {acc.clientId || '—'}</div>
                  <div>
                    <strong>Nom :</strong>{' '}
                    {client
                      ? `${client.firstName || ''} ${client.lastName || ''}`.trim() || '—'
                      : '—'}
                  </div>
                  <div><strong>NIF/CIN :</strong> {client?.nifCin || '—'}</div>
                  <div><strong>Téléphone :</strong> {client?.phone || '—'}</div>
                  <div><strong>Email :</strong> {client?.email || '—'}</div>
                  <div><strong>Adresse :</strong> {client?.address || '—'}</div>
                </Col>
              ) : (
                <Col md={7}>
                  <h5 className="mb-3">Mineur</h5>
                  <div><strong>ID mineur :</strong> {acc.minorId || '—'}</div>
                  <div>
                    <strong>Nom :</strong>{' '}
                    {minor
                      ? `${minor.firstName || ''} ${minor.lastName || ''}`.trim() || '—'
                      : '—'}
                  </div>
                  <div><strong>Date de naissance :</strong> {minor?.birthDate ? fmtDate(minor.birthDate) : '—'}</div>
                  <div><strong>Email :</strong> {minor?.email || '—'}</div>
                  <div><strong>Téléphone :</strong> {minor?.phone || '—'}</div>
                  <div><strong>Relation :</strong> {minor?.relation || '—'}</div>
                  <div><strong>Parent (ID) :</strong> {minor?.parentId || '—'}</div>
                  <div><strong>Notes :</strong> {minor?.notes || '—'}</div>
                </Col>
              )}
            </Row>

            <hr />
            <h5 className="mb-3">Historique des transactions</h5>
            {txs.length === 0 ? (
              <Alert variant="light">
                Aucune transaction enregistrée pour ce compte.
              </Alert>
            ) : (
              <Table striped bordered hover size="sm" responsive>
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Date</th>
                    <th>Type</th>
                    <th>Montant</th>
                    <th>Solde après</th>
                    <th>Code transaction</th>
                    <th>Note</th>
                  </tr>
                </thead>
                <tbody>
                  {txs.map((t) => (
                    <tr key={t.id}>
                      <td>{t.id}</td>
                      <td>{t.createdAt ? fmtDateTime(t.createdAt) : ''}</td>
                      <td>{t.type}</td>
                      <td>{fmtMoney(t.amount)}</td>
                      <td>{fmtMoney(t.balanceAfter)}</td>
                      <td>
                        <Badge bg="dark">{t.codeTransaction || '—'}</Badge>
                      </td>
                      <td>{t.description || ''}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </>
        )}
      </Modal.Body>

      <Modal.Footer>
        {acc && <ViewPdfButton getDocument={getDocument} className="me-2" />}
        <Button variant="secondary" onClick={onHide}>
          Fermer
        </Button>
      </Modal.Footer>
    </Modal>
  );
}